import React from 'react';

interface TestProgressPanelProps {
  // Props mantidas para compatibilidade (não mais exibidas na UI principal)
  currentQuestionNumber: number;
  totalQuestions: number;
  correctAnswers: number;
  timeElapsed?: string;
  difficultyLevel?: string;

  // Campos de debug (opcionais)
  phase?: string;              // "Phase1" | "Phase2"
  phaseGroup?: string;         // "A" | "B" | "C" | "D"
  currentCycleIndex?: number;  // ciclo atual (1, 2, ...)
  cycleCorrect?: number;       // acertos no ciclo atual
  cycleTotal?: number;         // respondidas no ciclo atual
  pendingInCycle?: number;     // pendentes no ciclo atual
  poolCandidates?: number;     // candidatos totais no range atual
  decisionHint?: string;       // ex: "Drop ≤75%", "Exaustão", "Continua"
  minLevel?: number;           // nível mínimo do range atual
  maxLevel?: number;           // nível máximo do range atual
  levelRangeSize?: number;     // tamanho do range (maxLevel - minLevel + 1)

  /** When false, panel is hidden (invisible + aria-hidden). Default true. Set via config.showLevelTestProgressPanel (VITE_LEVEL_TEST_SHOW_PROGRESS_PANEL). */
  visible?: boolean;
}

const TestProgressPanel: React.FC<TestProgressPanelProps> = ({
  // Props antigas (mantidas para compatibilidade)
  // currentQuestionNumber,
  // totalQuestions,
  // correctAnswers,
  timeElapsed = '0:00',
  // difficultyLevel,

  // Props de debug
  phase,
  phaseGroup,
  currentCycleIndex,
  cycleCorrect,
  cycleTotal,
  pendingInCycle,
  poolCandidates,
  decisionHint,
  minLevel,
  maxLevel,
  levelRangeSize,
  visible = true,
}) => {
  // Dados de debug vindos do backend (LevelTestProgressDebug)
  const debugData = {
    phase: phase,
    phaseGroup: phaseGroup,
    currentCycleIndex,
    cycleCorrect,
    cycleTotal,
    pendingInCycle,
    poolCandidates,
    decisionHint,
    minLevel,
    maxLevel,
    levelRangeSize,
  };

  const cycleAccuracy =
    debugData.cycleTotal && debugData.cycleTotal > 0 && debugData.cycleCorrect != null
      ? ((debugData.cycleCorrect / debugData.cycleTotal) * 100).toFixed(1)
      : undefined;

  // Só mostra painel de debug se existir algum dado real
  const showDebug =
    !!debugData.phase ||
    !!debugData.phaseGroup ||
    !!debugData.currentCycleIndex ||
    !!debugData.cycleTotal ||
    !!debugData.poolCandidates ||
    !!debugData.decisionHint ||
    debugData.minLevel != null ||
    debugData.maxLevel != null;

  return (
    <div
      className={`fixed left-4 top-1/2 transform -translate-y-1/2 z-[9999] ${!visible ? 'invisible' : ''}`}
      aria-hidden={!visible}
    >
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-3 w-40">
        {/* Header simples */}
        <div className="mb-2">
          <h3 className="text-xs font-semibold text-gray-900">
            Progresso
          </h3>
        </div>

        {/* Tempo (única info "de aluno" mantida) */}
        <div className="flex justify-between items-center mb-1">
          <span className="text-[10px] text-gray-600">Tempo</span>
          <span className="font-semibold text-gray-900 text-xs">
            {timeElapsed}
          </span>
        </div>

        {showDebug && (
          <>
            <div className="my-2 border-t border-gray-200" />

            <div className="space-y-1 text-[10px] text-gray-700">
              <div className="font-semibold text-[10px] text-gray-500 uppercase tracking-wide mb-1">
                Debug
              </div>

              <div className="flex justify-between">
                <span className="text-[10px] text-gray-500">Fase</span>
                <span className="font-medium">{debugData.phase ?? '-'}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-[10px] text-gray-500">Grp</span>
                <span className="font-medium">{debugData.phaseGroup ?? '-'}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-[10px] text-gray-500">Ciclo</span>
                <span className="font-medium">
                  {debugData.currentCycleIndex != null ? debugData.currentCycleIndex : '-'}
                </span>
              </div>

              <div className="flex flex-col">
                <span className="text-[10px] text-gray-500 mb-0.5">
                  C/T (Acc)
                </span>
                <span className="font-medium text-right">
                  {debugData.cycleCorrect ?? 0}/{debugData.cycleTotal ?? 0}
                  {cycleAccuracy && ` (${cycleAccuracy}%)`}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-[10px] text-gray-500">
                  Pend
                </span>
                <span className="font-medium">
                  {debugData.pendingInCycle != null ? debugData.pendingInCycle : 0}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-[10px] text-gray-500">
                  Pool
                </span>
                <span className="font-medium">
                  {debugData.poolCandidates != null ? debugData.poolCandidates : 0}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-[10px] text-gray-500">Lvl Range</span>
                <span className="font-medium">
                  {debugData.minLevel != null && debugData.maxLevel != null
                    ? `${debugData.minLevel}-${debugData.maxLevel} (${debugData.levelRangeSize ?? 0})`
                    : '-'}
                </span>
              </div>

              <div className="flex flex-col mt-1">
                <span className="text-[10px] text-gray-500 mb-0.5">Decisão</span>
                <span
                  className="font-medium text-right truncate"
                  title={debugData.decisionHint}
                >
                  {debugData.decisionHint ?? '-'}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TestProgressPanel;
